import React, { useState, useEffect } from 'react';
import { Sparkles, AlertTriangle, Info, Circle, CircleDot } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Components/ui/tooltip';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../Components/ui/carousel";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';

interface HealthPulseItem {
  area: string;
  score: number;
  initialScore: number;
  priority?: boolean;
  trendPercentage?: number;
}

interface HabitTrend {
  habit: string;
  icon: string;
  trend: string;
  direction: 'up' | 'down' | 'stable';
  current: number;
  target: number;
  status: 'improved' | 'declined' | 'stable';
}

interface HealthPulseProps {
  data: HealthPulseItem[];
  mostImproved?: string;
  focusArea?: string;
  weeklyInsights?: string[];
  improvementSummaries?: string[];
  needsAttentionSummaries?: string[];
  habitTrends?: HabitTrend[];
}

// Default data to display when no data is provided
const defaultHealthData: HealthPulseItem[] = [
  { area: 'Sleep', score: 0, initialScore: 0 },
  { area: 'Nutrition', score: 0, initialScore: 0 },
  { area: 'Exercise', score: 0, initialScore: 0 },
  { area: 'Stress', score: 0, initialScore: 0 },
  { area: 'Hydration', score: 0, initialScore: 0 },
  { area: 'Mood', score: 0, initialScore: 0 },
  { area: 'Energy', score: 0, initialScore: 0 },
  { area: 'Social', score: 0, initialScore: 0 },
];

export const HealthPulse: React.FC<HealthPulseProps> = ({ 
  data = [], 
  weeklyInsights = [],
  improvementSummaries = [],
  needsAttentionSummaries = [],
}) => {
  const [currentInsight, setCurrentInsight] = useState(0);
  const [api, setApi] = useState<any>(null);
  const { t } = useTranslation();
  
  // Use default data if data array is empty
  const displayData = data.length > 0 ? data : defaultHealthData;
        
  const formattedData = displayData.map(item => {     
    return {
      ...item,
      area: t(`dashboard.healthPulse.areas.${item.area}`, item.area)
    };
  });

  // Auto-rotate carousel every 2 seconds
  useEffect(() => {
    if (!api || weeklyInsights.length <= 1) return;
    
    const intervalId = setInterval(() => {
      const currentIndex = api.selectedScrollSnap();
      const nextIndex = (currentIndex + 1) % weeklyInsights.length;
      api.scrollTo(nextIndex);
      setCurrentInsight(nextIndex);
    }, 2000);
    
    return () => clearInterval(intervalId);
  }, [api, weeklyInsights.length]);
  
  // Handle API setup for the carousel
  const handleApiChange = (newApi: any) => {
    if (!newApi) return;
    
    setApi(newApi);
    
    newApi.on("select", () => {
      setCurrentInsight(newApi.selectedScrollSnap());
    });
  };

  return (
    <TooltipProvider>
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-medium text-gray-800">{t('dashboard.healthPulse.patternOverview', 'Health Pattern Overview')}</h3>
            <Tooltip>
              <TooltipTrigger>
                <div className="text-xs text-gray-500 flex items-center">
                  <Info className="w-3 h-3 mr-1" />
                  {data.length > 0 
                    ? t('dashboard.healthPulse.basedOnCheckIns', 'Based on your last 3 check-ins') 
                    : t('dashboard.healthPulse.noDataAvailable', 'No health data available yet')}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-[200px]">
                  {data.length > 0 
                    ? t('dashboard.healthPulse.chartCompares', 'This chart compares your current health metrics with those from 2 weeks ago.') 
                    : t('dashboard.healthPulse.startTracking', 'Start tracking your health metrics to see your progress here.')}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex justify-center">
            <div className="h-[260px] w-full max-w-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={formattedData}>
                  <defs>
                    <linearGradient id="priorityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f87171" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="improvingGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="neutralGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1C6E4A" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#1C6E4A" stopOpacity={0.4} />
                    </linearGradient>
                    <linearGradient id="mostImprovedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="focusAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fb7185" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                  
                  <PolarGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                  <PolarAngleAxis 
                    dataKey="area" 
                    tick={(props) => {
                      const { x, y, payload, textAnchor } = props;
                      
                      return (
                        <g>
                          <text 
                            x={x} 
                            y={y} 
                            textAnchor={textAnchor} 
                            stroke="none" 
                            fill="#4b5563" 
                            fontSize={12} 
                            fontWeight={500}
                          >
                            {payload.value}
                          </text>
                        </g>
                      );
                    }}
                    stroke="#e5e7eb"
                    tickLine={false}
                  />
                  <PolarRadiusAxis 
                    domain={[0, 100]} 
                    tickCount={5}
                    tick={{ fontSize: 10 }}
                    stroke="#9ca3af"
                    axisLine={false}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Radar
                    name="2 Weeks Ago"
                    dataKey="initialScore"
                    stroke="#94a3b8"
                    fill="#94a3b8"
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                  />
                  <Radar
                    name="Current"
                    dataKey="score"
                    stroke="#1C6E4A"
                    fillOpacity={0.45}
                    strokeWidth={2}
                  />
                  <Legend 
                    align="center" 
                    verticalAlign="top"
                    height={30}
                    wrapperStyle={{ fontSize: '12px' }}
                    payload={[
                      { value: t('dashboard.healthPulse.current', 'Current'), color: '#1C6E4A' },
                      { value: t('dashboard.healthPulse.twoWeeksAgo', '2 Weeks Ago'), color: '#94a3b8' }
                    ]}
                  />
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}/100`, name]}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      padding: '8px 12px',
                      fontSize: '12px'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {data.length === 0 ? (
            <div className="text-center mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600">
                {t('dashboard.healthPulse.noDataYet', 'No health data available yet. Start tracking your health metrics to see insights here.')}
              </p>
            </div>
          ) : (
            <>              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                    <Sparkles className="w-4 h-4 mr-1 text-green-600" />
                    {t('dashboard.healthPulse.improvementsThisWeek', 'Improvements this week:')}
                  </h4>
                  {improvementSummaries.length > 0 ? (
                    <ul className="space-y-2.5">
                      {improvementSummaries.map((summary, idx) => (
                        <li key={idx} className="flex items-start text-sm text-green-700">
                          <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5 mr-2.5 flex-shrink-0" />
                          <span>{summary}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-green-700">{t('dashboard.healthPulse.needMoreSessions', 'Need more sessions to see your progress.')}</p>
                  )}
                </div>
                
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-amber-800 mb-2 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1 text-amber-600" />
                    {t('dashboard.healthPulse.needsAttention', 'Needs attention:')}
                  </h4>
                  {needsAttentionSummaries.length > 0 ? (
                    <ul className="space-y-2.5">
                      {needsAttentionSummaries.map((summary, idx) => (
                        <li key={idx} className="flex items-start text-sm text-amber-700">
                          <div className="h-2 w-2 rounded-full bg-amber-400 mt-1.5 mr-2.5 flex-shrink-0" />
                          <span>{summary}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-amber-700">{t('dashboard.healthPulse.needMoreSessionsAnalytics', 'Need more sessions to generate analytics.')}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-medium text-gray-800">{t('dashboard.healthPulse.weeklyPatterns', 'This Week\'s Key Patterns')}</h3>
          </div>
          
          {weeklyInsights.length > 0 ? (
            <>
              <Carousel
                className="w-full"
                setApi={handleApiChange}
              >
                <CarouselContent>
                  {weeklyInsights.map((insight, index) => (
                    <CarouselItem key={index}>
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <div>
                          <p className="text-blue-800 font-medium">
                            {insight}
                          </p>
                          <p className="mt-3 text-sm text-blue-700">
                            {t('dashboard.healthPulse.mentionedInCalls', 'This was mentioned in 3 of your last 4 calls.')}
                          </p>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              
              {/* Carousel indicators (dots) */}
              <div className="flex items-center justify-center mt-3">
                {weeklyInsights.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => api?.scrollTo(index)}
                    className="mx-1 focus:outline-none"
                  >
                    {index === currentInsight ? (
                      <CircleDot className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300" />
                    )}
                  </button>
                ))}
              </div>
              
              <div className="mt-3 text-xs text-gray-500 text-center">
                {t('dashboard.healthPulse.patternOrigin', 'Pattern origin: Based on voice conversation & streak data')}
              </div>
            </>
          ) : (
            <div className="text-center p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-blue-800">
                {t('dashboard.healthPulse.noWeeklyPatterns', 'No weekly patterns detected yet. Complete check-ins to generate insights.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default HealthPulse;
